import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import {
  ANNI_AGENDA, compleanniUnaVolta, daIso, delGiorno, doveHoCercato, giorniStriscia, giornoValido, grigliaMese, GIORNI_BREVI, INTESTAZIONE_MESE, isoGiorno, isoMese,
  limitiData, MESI, MESI_TITOLO, meseConAnno, meseValido, messaggioErrore, nellaFinestra, perGiorno, quantiTesto, TIPI, titoloGiorno,
  vociCella, weekend, indiceAdesso,
} from '../../utils/agenda';
import { adessoRoma, linkChiama } from '../../utils/percorso';
import Icona from '../../components/cliente/Icone';
import Tuttofare from '../../components/staff/Tuttofare';
import CardVoce, { EsitoVisita, linkVoce } from '../../components/staff/CardVoce';
import EsitoTelefonata from '../../components/staff/EsitoTelefonata';
import CosaVoce from '../../components/staff/CosaVoce';
import FissaVisita from '../../components/staff/FissaVisita';
import { Suggerito } from '../../components/staff/Consiglio';
import { Iniziale, usePersone } from '../../components/staff/Persone';

// /admin/agenda (admin e collaboratori): la bozza design/agenda-v9.html. Vista Giorno con la striscia dei 14 giorni
// e la linea del tempo, vista Mese; filtro per persona e ricerca su tutti i giorni; aggiunta rapida col Tuttofare;
// in ritardo, prossimi giorni e giro di oggi. Dati: backend/agenda.py.

// Giorno e ora dell'agenda sono quelli di Roma (come le date che manda il server), non quelli del telefono: con un
// fuso avanti l'agenda si apriva sul giorno dopo e la riga "adesso" stava all'ora sbagliata. L'orologio si rilegge
// ogni mezzo minuto, così alla mezzanotte la pagina passa da sola al giorno nuovo (come il calendario del cliente).
const OGNI_MEZZO_MINUTO = 30000;
const oraAdesso = (d = adessoRoma()) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

// Quanto il server accetta nella ricerca: `q: Optional[str] = Query(default=None, max_length=100)`
// (backend/agenda.py). Il campo non aveva limite: oltre i cento caratteri il server rispondeva 422 e il riquadro
// diceva "Non riesco a cercare in agenda. Riprovate tra poco.", ma riprovare non serviva a niente. È lo stesso
// difetto della ricerca globale (ottavo giro 6): si taglia a quello che il server accetta, incollato compreso
// (il testo incollato passa sopra `maxLength`).
export const MAX_CERCA = 100;

// Quanto il server tiene di "Cosa fare" aggiungendo un'attività: `testo_o_400(dati.get("titolo"), "Il titolo", 200)`
// (backend/agenda.py, POST /api/agenda). Il campo non aveva limite e il taglio era muto: 1200 caratteri diventavano
// 200 e il toast diceva solo "In agenda". (Modificando la stessa attività il server ne tiene mille: la differenza è
// sua, non della pagina — la pagina segue chi salva, come per la nota di "Fissa visita" nel nono giro B3.)
export const MAX_TITOLO_NUOVO = 200;

// Quanto si aspetta l'elenco delle persone prima di usare lo stesso il "?persona=" dell'indirizzo. L'elenco
// (/api/staff/persone) parte al montaggio insieme a tutto il resto e arriva subito: l'attesa serve solo a non
// chiedere il giorno con un id che non si può ancora giudicare. Se non arriva (staff/persone rotta) si prova lo
// stesso, e l'ultima parola torna al server (400/403 ripuliscono l'indirizzo), invece di restare su "carico…".
export const ATTESA_PERSONE = 4000;

// L'agenda del giorno: "Carico…" mentre arriva, con un errore lo dice e si può riprovare (prima diceva "Niente in
// agenda oggi" e "0 cose", come una giornata vuota)
const StatoAgenda = ({ stato, onRiprova }) => {
  if (stato === 'attesa') return <p className="vuoto" role="status" data-testid="agenda-attesa" style={{ padding: '1rem 0' }}>Carico l'agenda…</p>;
  if (stato !== 'errore') return null;
  return (
    <p className="vuoto" role="alert" data-testid="agenda-errore" style={{ padding: '1rem 0' }}>
      Non riesco a caricare l'agenda.{' '}
      <button type="button" className="link" onClick={onRiprova}>Riprova</button>
    </p>
  );
};

// Gli altri tre riquadri (In ritardo, Giro, Suggeriti) chiamano il server per conto loro: se uno non risponde il
// riquadro spariva e la pagina sembrava quella di chi è in pari. Ognuno ha il suo stato, come `statoVoci`.
const StatoRiquadro = ({ stato, cosa, onRiprova, nome }) => {
  if (stato === 'attesa') return <p className="vuoto" role="status" data-testid={`${nome}-attesa`}>Carico {cosa}…</p>;
  if (stato !== 'errore') return null;
  return (
    <p className="vuoto" role="alert" data-testid={`${nome}-errore`}>
      Non riesco a caricare {cosa}.{' '}
      <button type="button" className="link" onClick={onRiprova}>Riprova</button>
    </p>
  );
};

// Elenchi tagliati: "e altre 6…" apre il resto, invece di far sparire le righe in silenzio
const Ancora = ({ quante, una, tante, onApri, nome }) => (quante > 0 ? (
  <button type="button" className="link ancora" onClick={onApri} data-testid={`ancora-${nome}`}>
    e {quante === 1 ? una : `${tante} ${quante}`}…
  </button>
) : null);

// Una riga di "In ritardo": la riga dice cosa fare, e da lì si fa. La visita senza esito ha "Andata bene"/"Andata
// male" come la sua card; la telefonata scaduta ha "Esito" (i sette esiti, come nella card del giorno) accanto a
// "Chiama". Prima la telefonata aveva il solo "Chiama" e la data non era un link: un'attesa del collega scaduta o un
// richiamo al decimo tentativo si potevano solo rifare al telefono, e per dire com'era andata bisognava tornare al
// giorno e ritrovare la card. Quando l'esito non si può dare (la telefonata non ha né immobile né agenzia: il server
// non lo prenderebbe) resta almeno "Apri", che porta al giorno.
const RigaRitardo = ({ v, isAdmin, onCambia, onApri }) => {
  const [esito, setEsito] = useState(false);
  const telefonata = v.tipo === 'telefonata';
  const conEsito = telefonata && Boolean(v.immobile_id || v.agenzia_id);
  const tel = telefonata ? linkChiama(v.telefono) : null;
  const visitaDaChiudere = v.tipo === 'visita' && Boolean(v.rif?.visita_id);
  // due bottoni nella terza colonna strizzano la riga in una colonnina di poche parole (il riquadro è la colonna
  // stretta della pagina anche a 1440 px): vanno a capo sotto il testo, come già fa la visita
  const sotto = visitaDaChiudere || conEsito || Boolean(tel);
  return (
    <div className={sotto ? 'con-esito' : undefined} data-testid={`ritardo-${v.id}`}>
      <div className="n">{v.giorni}<small>{v.giorni === 1 ? 'giorno' : 'giorni'}</small></div>
      <div style={{ minWidth: 0 }}>
        {v.cosa ? <CosaVoce cosa={v.tipo === 'visita' ? { ...v.cosa, azione: v.cosa.agenzia ? "Dare l'esito della visita con" : "Dare l'esito della visita" } : v.cosa} tipo={v.tipo} />
          : <b>{v.tipo === 'visita' ? `Esito mancante · ${v.immobile?.indirizzo || v.titolo}` : v.titolo}</b>}
        <span>
          {!v.cosa && v.cliente_breve ? `${v.cliente_breve} · ` : ''}
          {/* la data è il link al suo giorno in agenda: la riga con "Esito" e "Chiama" non ha più "Apri", e da lì si
              deve poter arrivare alla card (prima la data era testo morto, per tutti i tipi) */}
          <button type="button" className="link-giorno" onClick={() => onApri(v.data)} title="Apri quel giorno in agenda">
            {/* l'elisione davanti a 8 e 11: prima "del 8 settembre", "del 11 settembre" (terzo giro B1) */}
            {delGiorno(`${daIso(v.data).getDate()} ${MESI[daIso(v.data).getMonth()]}${daIso(v.data).getFullYear() !== adessoRoma().getFullYear() ? ` ${daIso(v.data).getFullYear()}` : ''}`)}
          </button>
        </span>
      </div>
      {visitaDaChiudere ? <EsitoVisita voce={v} onCambia={onCambia} piccoli />
        : (
          <span className="azioni-ritardo">
            {conEsito && <button type="button" className="btn sm p" onClick={() => setEsito(!esito)} aria-expanded={esito}>Esito</button>}
            {tel && <a className="btn sm" href={tel}>Chiama</a>}
            {!conEsito && <button type="button" className="btn sm" onClick={() => onApri(v.data)}>Apri</button>}
          </span>
        )}
      {esito && <EsitoTelefonata voce={v} isAdmin={isAdmin} onFatto={() => { setEsito(false); onCambia(); }} />}
    </div>
  );
};

const Striscia = ({ giorni, scelto, voci, onScegli }) => {
  const per = perGiorno(voci);
  return (
    <div className="strip r" role="tablist" aria-label="Giorni">
      {giorni.map((g) => {
        const d = daIso(g);
        const tipi = [...new Set((per[g] || []).filter((v) => v.stato !== 'annullata').map((v) => v.tipo))].slice(0, 4);
        return (
          <button type="button" role="tab" key={g} aria-selected={g === scelto} className={`dp${g === scelto ? ' on' : ''}${weekend(g) ? ' we' : ''}`} onClick={() => onScegli(g)}>
            <small>{GIORNI_BREVI[d.getDay()]}</small><b>{d.getDate()}</b><i>{tipi.map((t) => <u key={t} className={`c-${t}`} />)}</i>
          </button>
        );
      })}
    </div>
  );
};

const Mese = ({ anno, mese, voci, oggi, onGiorno }) => {
  const celle = grigliaMese(anno, mese);
  const per = perGiorno(voci.filter((v) => v.stato !== 'annullata'));
  return (
    <section className="r" data-testid="vista-mese">
      <div className="mg">
        {INTESTAZIONE_MESE.map((g) => <div key={g} className="wd">{g}</div>)}
        {celle.map((c) => {
          const suoi = per[c.iso] || [];
          const { mostrate, altre } = vociCella(suoi);
          return (
            <button type="button" key={c.iso} className={`dc${c.fuori ? ' o' : ''}${c.iso === oggi ? ' t' : ''}`} onClick={() => onGiorno(c.iso)}
              aria-label={`${c.giorno} ${MESI[daIso(c.iso).getMonth()]}: ${quantiTesto(suoi.length, 'cosa', 'cose')}`} data-testid={`cella-${c.iso}`}>
              <div className="n">{c.giorno}{c.iso === oggi && <small>oggi</small>}</div>
              {mostrate.map((v) => (
                <div key={v.id} className={`e${v.tipo === 'scadenza' ? ' late' : ''}`}>
                  <i className={`c-${v.tipo}`} /><span className="t">{v.ora ? `${v.ora} ` : ''}{v.titolo}</span>
                  {v.assegnata && <Iniziale persona={v.assegnata} taglia={12} />}
                </div>
              ))}
              {altre > 0 && <div className="more">+{altre} {altre === 1 ? 'altro' : 'altri'}</div>}
              <div className="dots">{suoi.slice(0, 6).map((v) => <u key={v.id} className={`c-${v.tipo}`} />)}</div>
            </button>
          );
        })}
      </div>
      <div className="legend">
        {['visita', 'telefonata', 'scadenza', 'rogito', 'documento', 'compleanno'].map((t) => <span key={t}><i className={`c-${t}`} />{TIPI[t].nome}</span>)}
      </div>
    </section>
  );
};

// Tipo "Visita": si apre la finestra "Fissa visita" con il calendario (onVisita).
// "A chi" c'è dall'inizio (di base io): prima l'attività finiva sempre a chi la scriveva e non si poteva più
// riassegnare, perché nemmeno la card aveva "Modifica".
const AggiungiAMano = ({ giorno, oggi, chiudi, onFatto, onVisita }) => {
  const { persone, ioId } = usePersone();
  const [dati, setDati] = useState({ tipo: 'altro', titolo: '', data: giorno, ora: '', assegnata_a: '' });
  useEffect(() => { setDati((d) => (d.assegnata_a || !ioId ? d : { ...d, assegnata_a: ioId })); }, [ioId]);
  const campo = { border: '1px solid var(--line2)', borderRadius: 10, padding: '.45rem .6rem' };
  const limiti = limitiData(oggi);
  const invia = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/agenda`, { ...dati, assegnata_a: dati.assegnata_a || undefined, tutto_il_giorno: !dati.ora });
      toast.success('In agenda');
      chiudi();
      onFatto();
    } catch (err) { toast.error(messaggioErrore(err, 'Attività non aggiunta. Riprovate.')); }
  };
  return (
    <form className="quick-risposta" onSubmit={invia} data-testid="aggiungi-a-mano" style={{ marginTop: 0 }}>
      <select value={dati.tipo} onChange={(e) => (e.target.value === 'visita' && onVisita ? onVisita() : setDati({ ...dati, tipo: e.target.value }))} aria-label="Tipo" style={campo}>
        {['altro', 'telefonata', 'visita', 'scadenza', 'nota'].map((t) => <option key={t} value={t}>{TIPI[t].nome}</option>)}
      </select>
      {/* il campo si ferma a quello che il server tiene: oltre tagliava lui in silenzio (il taglio vale anche per
          l'incollato, che passa sopra `maxLength`) */}
      <input value={dati.titolo} maxLength={MAX_TITOLO_NUOVO} onChange={(e) => setDati({ ...dati, titolo: e.target.value.slice(0, MAX_TITOLO_NUOVO) })}
        placeholder="Cosa fare" aria-label="Cosa fare" required style={{ ...campo, flex: 1, minWidth: 180 }} />
      {/* il giorno resta dentro la finestra del server (cinque anni indietro e avanti): fuori rispondeva 400 */}
      <input type="date" value={dati.data} min={limiti.min} max={limiti.max} onChange={(e) => setDati({ ...dati, data: e.target.value })} aria-label="Giorno" style={{ ...campo, padding: '.4rem' }} />
      <input type="time" value={dati.ora} onChange={(e) => setDati({ ...dati, ora: e.target.value })} aria-label="Ora" style={{ ...campo, padding: '.4rem' }} />
      <select value={dati.assegnata_a} onChange={(e) => setDati({ ...dati, assegnata_a: e.target.value })} aria-label="A chi" style={campo}>
        {persone.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
      </select>
      <button type="submit" className="btn sm p">Aggiungi</button>
      <button type="button" className="btn sm" onClick={chiudi}>Annulla</button>
    </form>
  );
};

const Agenda = () => {
  const { userType } = useAuth();
  const isAdmin = userType === 'admin';
  const [params, setParams] = useSearchParams();
  const [adesso, setAdesso] = useState(adessoRoma);
  useEffect(() => {
    const t = setInterval(() => setAdesso(adessoRoma()), OGNI_MEZZO_MINUTO);
    return () => clearInterval(t);
  }, []);
  const oggi = isoGiorno(adesso);
  // "?giorno=pippo" o "?giorno=2026-13-45": si apre oggi e l'indirizzo si ripulisce (prima il titolo diceva "NaN"
  // e "2026-13-45" apriva febbraio 2027). Il giorno dev'essere anche dentro la finestra del server (cinque anni):
  // con "?giorno=9999-12-31" le date di JS traboccavano e la striscia ripeteva dieci volte lo stesso giorno.
  const giornoChiesto = params.get('giorno');
  const giornoBuono = giornoValido(giornoChiesto, oggi);
  const giorno = giornoBuono ? giornoChiesto : oggi;
  // "?vista=lista", "?vista=settimana", "?vista=pippo": viste che non esistono. Si apre "Giorno" (come senza
  // parametro) e l'indirizzo si ripulisce, come si fa per "?giorno=" e "?mese=", invece di restare sporco con una
  // vista che non è quella che si vede.
  const vistaChiesta = params.get('vista');
  const vistaBuona = vistaChiesta === 'mese' || vistaChiesta === 'giorno';
  const vista = vistaChiesta === 'mese' ? 'mese' : 'giorno';
  const { persone, ioId } = usePersone();
  // Il filtro persona sta nell'indirizzo ("?persona=<id>"), come "?giorno=", "?vista=" e "?mese=" (e come il
  // "?persona=" di "Oggi", terzo giro A5): prima stava solo in memoria, così il chip si accendeva ma la ricarica
  // lo perdeva, una seconda scheda si apriva su un altro filtro e "Indietro" usciva dalla pagina invece di tornare
  // alla persona di prima.
  // Senza parametro vale quello che si apriva prima: l'admin vede tutti, il collaboratore la sua agenda
  // ("I miei clienti" per vedere anche le visite accompagnate da altri), che è anche quello che fa il server senza
  // filtro. Il chip predefinito quindi non sporca l'indirizzo.
  const personaChiesta = params.get('persona');
  const chiesta = (personaChiesta || '').trim();
  const predefinita = isAdmin ? 'tutti' : (ioId || null);
  // "tutti" e "tutte" sono la stessa scelta per il server (backend/agenda.py, `tutti_o_tutte`)
  const tutteLeVoci = ['tutti', 'tutte'].includes(chiesta.toLowerCase());
  // Un id si può giudicare solo quando si sa chi sono le persone. Prima l'elenco ancora vuoto valeva "va bene" e il
  // filtro partiva lo stesso: con "?persona=u-inesistente" i cinque riquadri del giorno chiedevano al server con
  // quell'id (cinque 400, o cinque 403 con la persona di un altro, e cinque righe rosse in console) e solo dopo
  // l'indirizzo si ripuliva. Come "?giorno=", "?mese=" e "?vista=", si ripulisce PRIMA di chiedere qualcosa:
  // finché l'elenco non è arrivato non parte niente (l'attesa è quella di /api/staff/persone, che parte al
  // montaggio insieme al resto).
  const personeNote = persone.length > 0;
  const [personeTardi, setPersoneTardi] = useState(false);
  useEffect(() => {
    if (personeNote) return undefined;
    // se l'elenco non arriva (staff/persone rotta) non si resta su "carico…" per sempre: si prova lo stesso e
    // l'ultima parola torna al server, com'era prima
    const t = setTimeout(() => setPersoneTardi(true), ATTESA_PERSONE);
    return () => clearTimeout(t);
  }, [personeNote]);
  const aspettoPersone = Boolean(chiesta) && !tutteLeVoci && !personeNote && !personeTardi;
  const personaBuona = !chiesta || tutteLeVoci || !personeNote || persone.some((pp) => pp.id === chiesta);
  const persona = aspettoPersone || !chiesta || !personaBuona ? predefinita
    : (tutteLeVoci ? 'tutti' : chiesta);
  const [cerca, setCerca] = useState('');
  const [voci, setVoci] = useState([]);
  const [statoVoci, setStatoVoci] = useState('attesa');  // 'attesa' | 'errore' | 'pronto'
  const [trovate, setTrovate] = useState(null);
  // i giorni che la ricerca ha guardato davvero: li decide il server e li rimanda indietro con i risultati
  // (`dal`/`al`), e la frase li dice invece di promettere sempre "tutti i giorni"
  const [finestraCerca, setFinestraCerca] = useState(null);
  const [erroreCerca, setErroreCerca] = useState(false);
  const [ritardo, setRitardo] = useState([]);
  const [statoRitardo, setStatoRitardo] = useState('attesa');
  const [suggeriti, setSuggeriti] = useState([]);
  const [statoSuggeriti, setStatoSuggeriti] = useState('attesa');
  const [consigli, setConsigli] = useState({});
  const [giro, setGiro] = useState(null);
  const [statoGiro, setStatoGiro] = useState('attesa');
  const [aMano, setAMano] = useState(false);
  const [visita, setVisita] = useState(false);
  const [versione, setVersione] = useState(0);
  const [tutteRitardo, setTutteRitardo] = useState(false);
  const [tutteTrovate, setTutteTrovate] = useState(false);
  const ricarica = useCallback(() => setVersione((v) => v + 1), []);
  const d = daIso(giorno);

  const striscia = useMemo(() => giorniStriscia(differenza(giorno, oggi) >= 0 && differenza(giorno, oggi) < 12 ? oggi : giorno), [giorno, oggi]);
  const imposta = useCallback((nuovi, opzioni) => setParams((vecchi) => {
    const tutti = { ...Object.fromEntries(vecchi), ...nuovi };
    Object.keys(tutti).forEach((k) => { if (tutti[k] == null) delete tutti[k]; });
    return tutti;
  }, opzioni), [setParams]);
  // il chip scrive la scelta nell'indirizzo; quella predefinita lo lascia pulito (è quella che si apre senza)
  const setPersona = useCallback((id, opzioni) => imposta({ persona: !id || id === predefinita ? null : id }, opzioni), [imposta, predefinita]);
  const p = persona === 'tutti' ? undefined : persona;
  // la giornata di un collega: il filtro è su qualcuno che non sono io (con "Tutti" e con il proprio chip il
  // tuttofare scrive nella propria agenda, com'è giusto)
  const altrui = persona && persona !== 'tutti' && persona !== ioId ? persona : null;

  // Il mese sfogliato sta nell'indirizzo ("?mese=2026-11"): "Indietro" torna al mese di prima e poi esce dalla
  // vista, e la ricarica riapre il mese che si guardava (prima tornava sempre a quello del giorno scelto).
  // Un mese che non vale ("pippo", "2026-13", "99999-01", "0000-01") apre il mese del giorno scelto e si toglie
  // dall'indirizzo, come si fa per "?giorno=".
  const meseChiesto = params.get('mese');
  const meseBuono = useMemo(() => meseValido(meseChiesto, oggi), [meseChiesto, oggi]);
  const meseVisto = useMemo(() => {
    const dal = daIso(giorno);
    return meseBuono || { anno: dal.getFullYear(), mese: dal.getMonth() };
  }, [meseBuono, giorno]);
  const vaAlMese = ({ anno, mese }) => imposta({ mese: isoMese({ anno, mese }) });
  // le frecce non escono dalla finestra dei cinque anni: fuori di lì l'indirizzo si ripulirebbe e la vista
  // tornerebbe indietro da sola
  const mesePossibile = (m) => Boolean(meseValido(isoMese(m), oggi));

  useEffect(() => {
    if (giornoChiesto != null && !giornoBuono) imposta({ giorno: null }, { replace: true });
    if (meseChiesto != null && !meseBuono) imposta({ mese: null }, { replace: true });
    if (vistaChiesta != null && !vistaBuona) imposta({ vista: null }, { replace: true });
    // "?persona=" vuoto, o con un id che non è dello staff che si può vedere: si torna al filtro di partenza e
    // l'indirizzo si ripulisce, come per "?giorno=pippo" (le persone visibili sono quelle dei chip: backend/staff.py).
    // Finché non si sa chi sono le persone non si giudica niente: si aspetta (e intanto non si chiede niente al server)
    if (personaChiesta != null && !aspettoPersone && (!chiesta || !personaBuona)) imposta({ persona: null }, { replace: true });
  }, [giornoChiesto, giornoBuono, meseChiesto, meseBuono, vistaChiesta, vistaBuona, personaChiesta, chiesta, personaBuona, aspettoPersone, imposta]);

  // il periodo chiesto resta dentro la finestra del server, anche al bordo (fuori risponde 400 e l'agenda sembrava rotta)
  const periodo = useMemo(() => {
    const dentro = (dal, al) => ({ dal: nellaFinestra(dal, oggi), al: nellaFinestra(al, oggi) });
    if (vista === 'mese') {
      const celle = grigliaMese(meseVisto.anno, meseVisto.mese);
      return dentro(celle[0].iso, celle[celle.length - 1].iso);
    }
    return dentro(striscia[0], striscia[striscia.length - 1]);
  }, [vista, striscia, meseVisto.anno, meseVisto.mese, oggi]);

  useEffect(() => {
    setStatoVoci('attesa');
    // il filtro dell'indirizzo non è ancora giudicabile: si aspetta l'elenco delle persone invece di chiedere il
    // giorno con un id che magari non esiste
    if (aspettoPersone) return;
    axios.get(`${API}/agenda`, { params: { dal: periodo.dal, al: periodo.al, persona: p } })
      .then((r) => { setVoci(r.data.voci); setStatoVoci('pronto'); })
      .catch((e) => {
        // un "?persona=" che il server rifiuta (400 "Persona non valida", 403) non lascia l'agenda bloccata
        // sull'errore: si torna al filtro di partenza e l'indirizzo si ripulisce (come in "Oggi")
        if (chiesta && [400, 403].includes(e?.response?.status)) { setPersona(null, { replace: true }); return; }
        setVoci([]); setStatoVoci('errore');
      });
  }, [periodo, p, versione, chiesta, aspettoPersone, setPersona]);

  // "attesa" solo quando non c'è ancora niente da mostrare (primo caricamento, o dopo un errore): a ogni cambio di
  // giorno o di persona i riquadri restano come sono finché non arrivano quelli nuovi, senza lampeggiare
  useEffect(() => {
    setStatoRitardo((s) => (s === 'pronto' ? s : 'attesa'));
    setStatoSuggeriti((s) => (s === 'pronto' ? s : 'attesa'));
    if (aspettoPersone) return;
    axios.get(`${API}/agenda/in-ritardo`, { params: { persona: p } })
      .then((r) => { setRitardo(r.data); setStatoRitardo('pronto'); })
      .catch(() => { setRitardo([]); setStatoRitardo('errore'); });
    axios.get(`${API}/agenda/suggeriti`, { params: { persona: p } })
      .then((r) => { setSuggeriti(r.data); setStatoSuggeriti('pronto'); })
      .catch(() => { setSuggeriti([]); setStatoSuggeriti('errore'); });
    axios.get(`${API}/consigli`, { params: { persona: p } }).then((r) => setConsigli(Object.fromEntries(r.data.filter((c) => c.voce_id).map((c) => [c.voce_id, c])))).catch(() => setConsigli({}));
  }, [p, versione, aspettoPersone]);

  useEffect(() => {
    if (vista !== 'giorno' || aspettoPersone) return;
    setStatoGiro((s) => (s === 'pronto' ? s : 'attesa'));
    axios.get(`${API}/agenda/giro`, { params: { data: giorno, persona: p } })
      .then((r) => { setGiro(r.data); setStatoGiro('pronto'); })
      .catch(() => { setGiro(null); setStatoGiro('errore'); });
  }, [vista, giorno, p, versione, aspettoPersone]);

  useEffect(() => {
    if (cerca.trim().length < 2 || aspettoPersone) { setTrovate(null); setFinestraCerca(null); setErroreCerca(false); return undefined; }
    const t = setTimeout(() => {
      axios.get(`${API}/agenda`, { params: { q: cerca.trim().slice(0, MAX_CERCA), persona: p } })
        .then((r) => { setTrovate(r.data.voci); setFinestraCerca({ dal: r.data.dal, al: r.data.al }); setErroreCerca(false); })
        .catch(() => { setTrovate([]); setFinestraCerca(null); setErroreCerca(true); });
    }, 250);
    return () => clearTimeout(t);
  }, [cerca, p, versione, aspettoPersone]);

  // I risultati della ricerca: di ogni persona un compleanno solo (il server ne crea uno per anno e la ricerca guarda
  // un anno prima e uno dopo), e il conto in alto segue quello che si vede
  const risultati = useMemo(() => (trovate ? compleanniUnaVolta(trovate, oggi) : null), [trovate, oggi]);
  const delGiorno = voci.filter((v) => v.data === giorno).map((v) => ({ ...v, consiglio: consigli[v.id] }));
  const fatte = delGiorno.filter((v) => v.stato === 'fatta').length;
  const perPersona = persone.map((pp) => [pp.nome, delGiorno.filter((v) => v.assegnata_a === pp.id).length]).filter(([, n]) => n);
  const titolo = titoloGiorno(giorno, oggi);
  const prossimi = voci.filter((v) => v.data > giorno && v.stato === 'da_fare').slice(0, 6);
  // "Prossimi giorni" guarda solo i giorni già caricati (la striscia): dirlo, invece di far credere che più in là
  // non ci sia niente
  const giorniAvanti = Math.max(0, differenza(periodo.al, giorno));
  const indice = giorno === oggi ? indiceAdesso(delGiorno, oraAdesso(adesso)) : -1;
  const meseMostrato = vista === 'mese' ? meseVisto : { anno: d.getFullYear(), mese: d.getMonth() };

  return (
    <main className="st wrap" data-testid="agenda" style={{ maxWidth: 1120 }}>
      <div className="top r">
        <div><div className="k">Agenda</div><h1>{MESI_TITOLO[meseMostrato.mese]} <em>{meseMostrato.anno}</em></h1></div>
        <div className="people" role="group" aria-label="Di chi">
          <button type="button" className={`pp${persona === 'tutti' ? ' on' : ''}`} onClick={() => setPersona('tutti')}><i className="a col-tutti" style={{ width: 28, height: 28, fontStyle: 'normal' }}>✓</i>{isAdmin ? 'Tutti' : 'I miei clienti'}</button>
          {persone.map((pp) => (
            <button type="button" key={pp.id} className={`pp${persona === pp.id ? ' on' : ''}`} onClick={() => setPersona(pp.id)}><Iniziale persona={pp} taglia={28} />{pp.nome}</button>
          ))}
          {/* il campo si ferma a quello che il server accetta (MAX_CERCA): oltre rispondeva 422 */}
          <label className="srch"><Icona nome="cerca" /><input value={cerca} maxLength={MAX_CERCA} onChange={(e) => setCerca(e.target.value.slice(0, MAX_CERCA))} placeholder="Cerca cliente, agenzia, via…" aria-label="Cerca in agenda" /></label>
        </div>
        <div style={{ display: 'flex', gap: '.6rem', alignItems: 'center' }}>
          <div className="vseg" role="group" aria-label="Vista">
            <button type="button" className={vista === 'giorno' ? 'on' : undefined} onClick={() => imposta({ vista: 'giorno' })}>Giorno</button>
            <button type="button" className={vista === 'mese' ? 'on' : undefined} onClick={() => imposta({ vista: 'mese', mese: isoMese({ anno: d.getFullYear(), mese: d.getMonth() }) })}>Mese</button>
          </div>
          <button type="button" className="btn p" onClick={() => setAMano(!aMano)}><Icona nome="piu" />Aggiungi</button>
        </div>
      </div>

      {aMano && <AggiungiAMano giorno={giorno} oggi={oggi} chiudi={() => setAMano(false)} onFatto={ricarica} onVisita={() => { setAMano(false); setVisita(true); }} />}
      {visita && <FissaVisita onChiudi={() => setVisita(false)} onFatto={() => { setVisita(false); ricarica(); }} />}

      {risultati && (
        <section className="card r" style={{ marginBottom: '1.2rem' }} data-testid="risultati-agenda">
          <div className="ttl"><div className="i"><Icona nome="cerca" /></div><h2>“{cerca}” <small>{erroreCerca ? 'ricerca non riuscita' : `${quantiTesto(risultati.length, 'voce', 'voci')} ${doveHoCercato(finestraCerca, oggi)}`}</small></h2><button type="button" className="x" onClick={() => setCerca('')} aria-label="Chiudi la ricerca">✕</button></div>
          <div className="next">
            {erroreCerca && <p className="vuoto" role="alert">Non riesco a cercare in agenda. Riprovate tra poco.</p>}
            {!erroreCerca && risultati.length === 0 && <p className="vuoto">Niente in agenda.</p>}
            {(tutteTrovate ? risultati : risultati.slice(0, 30)).map((v) => (
              <div key={v.id}>
                {/* la ricerca va da un anno prima a un anno dopo: il mese porta l'anno quando non è quello in corso */}
                <div className="d">{daIso(v.data).getDate()}<small>{meseConAnno(v.data, oggi)}</small></div>
                <i className={`dot c-${v.tipo}`} />
                <button type="button" style={{ background: 'none', border: 0, color: 'inherit', textAlign: 'left', cursor: 'pointer', minWidth: 0 }}
                  onClick={() => { setCerca(''); imposta({ vista: 'giorno', giorno: v.data }); }}>
                  <b>{v.titolo}</b><span>{[v.ora, v.cliente_breve, v.assegnata?.nome, v.stato === 'fatta' ? 'fatta' : null].filter(Boolean).join(' · ')}</span>
                </button>
              </div>
            ))}
            {!erroreCerca && !tutteTrovate && (
              <Ancora quante={risultati.length - 30} una="un'altra" tante="altre" nome="ricerca" onApri={() => setTutteTrovate(true)} />
            )}
          </div>
        </section>
      )}

      {vista === 'mese' ? (
        <>
          <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.8rem' }}>
            {[{ verso: -1, m: meseVisto.mese === 0 ? { anno: meseVisto.anno - 1, mese: 11 } : { anno: meseVisto.anno, mese: meseVisto.mese - 1 } },
              { verso: 1, m: meseVisto.mese === 11 ? { anno: meseVisto.anno + 1, mese: 0 } : { anno: meseVisto.anno, mese: meseVisto.mese + 1 } }].map(({ verso, m }) => (
              <React.Fragment key={verso}>
                {verso === 1 && <button type="button" className="btn sm" onClick={() => vaAlMese({ anno: daIso(oggi).getFullYear(), mese: daIso(oggi).getMonth() })}>Oggi</button>}
                <button type="button" className="btn sm" disabled={!mesePossibile(m)} onClick={() => vaAlMese(m)}
                  title={mesePossibile(m) ? undefined : `L'agenda arriva a ${ANNI_AGENDA} anni da oggi`}>
                  {verso === -1 ? `← ${MESI[m.mese]}` : `${MESI[m.mese]} →`}
                </button>
              </React.Fragment>
            ))}
          </div>
          {statoVoci !== 'pronto' && <StatoAgenda stato={statoVoci} onRiprova={ricarica} />}
          <Mese anno={meseVisto.anno} mese={meseVisto.mese} voci={voci} oggi={oggi} onGiorno={(iso) => imposta({ vista: 'giorno', giorno: iso })} />
        </>
      ) : (
        <>
          <Striscia giorni={giorno < striscia[0] || giorno > striscia[striscia.length - 1] ? giorniStriscia(giorno) : striscia} scelto={giorno} voci={voci} onScegli={(g) => imposta({ giorno: g })} />
          {/* anche qui il filtro persona decide in che agenda finisce quello che si scrive (come in "Oggi"): con il
              chip di un collega acceso l'attività era mia e l'agenda mostrata non cambiava. "Tutti" e il proprio
              chip restano come prima */}
          <Tuttofare etichetta="" onFatto={ricarica} persona={altrui} nomePersona={altrui ? (persone.find((pp) => pp.id === altrui)?.nome || '') : ''}
            placeholder="Scrivi e Invio · “richiamare Zetaci per via Rosa del Sasso domani 10”" />
          <div className="two">
            <div>
              <div className="dayh r">
                <h2>{titolo.prefisso}<em>{titolo.resto}</em></h2>
                <span>{statoVoci === 'errore' ? 'non caricata' : statoVoci === 'attesa' ? 'carico…'
                  : `${quantiTesto(delGiorno.length, 'cosa', 'cose')}${fatte ? ` · ${fatte} ${fatte === 1 ? 'fatta' : 'fatte'}` : ''}${perPersona.length > 1 ? ` · ${perPersona.map(([nome, n]) => `${nome} ${n}`).join(', ')}` : ''}`}</span>
              </div>
              <div className="tl r">
                {statoVoci !== 'pronto' && delGiorno.length === 0 && <StatoAgenda stato={statoVoci} onRiprova={ricarica} />}
                {statoVoci === 'pronto' && delGiorno.length === 0 && <p className="vuoto" style={{ padding: '1rem 0' }}>Niente in agenda {giorno === oggi ? 'oggi' : 'questo giorno'}.</p>}
                {delGiorno.map((v, i) => (
                  <React.Fragment key={v.id}>
                    {i === indice && <div className="now" />}
                    <div className={`slot ${v.tipo}${v.stato !== 'da_fare' ? ' done' : ''}`}>
                      <div className={`h${v.ora ? '' : ' giorno'}`}>{v.ora || 'giorno'}</div>
                      <CardVoce voce={v} onCambia={ricarica} isAdmin={isAdmin} evidenziata={v.tipo === 'telefonata' && v.stato === 'da_fare' && v.tentativi > 0} />
                    </div>
                  </React.Fragment>
                ))}
                {indice === delGiorno.length && delGiorno.length > 0 && <div className="now" />}
              </div>
              {giorno === oggi && (suggeriti.length > 0 || statoSuggeriti !== 'pronto') && (
                <div style={{ marginTop: '1rem' }} data-testid="suggeriti">
                  <StatoRiquadro stato={statoSuggeriti} cosa="i suggeriti" onRiprova={ricarica} nome="suggeriti" />
                  {suggeriti.map((s) => <Suggerito key={s.id} s={s} onFatto={ricarica} />)}
                </div>
              )}
            </div>

            <div className="stack">
              {(ritardo.length > 0 || statoRitardo !== 'pronto') && (
                <section className="card ritardo r" data-testid="in-ritardo">
                  <div className="ttl"><div className="i r"><Icona nome="orologio" /></div><h2>In ritardo</h2></div>
                  <div className="late">
                    <StatoRiquadro stato={statoRitardo} cosa="le cose in ritardo" onRiprova={ricarica} nome="ritardo" />
                    {/* la riga dice cosa fare, e da lì si fa: la visita senza esito ha "Andata bene"/"Andata male"
                        come la sua card, la telefonata scaduta "Esito" e "Chiama" (prima c'era solo "Apri", che
                        portava al giorno e lasciava ritrovare la card) */}
                    {(tutteRitardo ? ritardo : ritardo.slice(0, 5)).map((v) => (
                      <RigaRitardo key={v.id} v={v} isAdmin={isAdmin} onCambia={ricarica} onApri={(giornoVoce) => imposta({ vista: 'giorno', giorno: giornoVoce })} />
                    ))}
                    {!tutteRitardo && <Ancora quante={ritardo.length - 5} una="un'altra" tante="altre" nome="ritardo" onApri={() => setTutteRitardo(true)} />}
                  </div>
                </section>
              )}
              {(statoGiro !== 'pronto' || (giro && giro.tappe.length > 0)) && (
                <section className="card giro r" data-testid="giro">
                  <div className="ttl"><div className="i g"><Icona nome="mappa" /></div><h2>Giro {giorno === oggi ? 'di oggi' : 'del giorno'}</h2>{giro?.link && <a href={giro.link} target="_blank" rel="noopener noreferrer">Google Maps →</a>}</div>
                  <StatoRiquadro stato={statoGiro} cosa="il giro" onRiprova={ricarica} nome="giro" />
                  <ol>
                    {(giro?.tappe || []).map((t) => <li key={t.voce_id}><div><b>{t.ora}</b> {t.indirizzo}<br /><span>{[t.cliente, t.minuti_dalla_sede ? `~${t.minuti_dalla_sede} min da ${giro.sede}` : null].filter(Boolean).join(' · ')}</span></div></li>)}
                  </ol>
                  {giro?.link && <a className="btn sm p" href={giro.link} target="_blank" rel="noopener noreferrer"><Icona nome="mappa" />Apri il giro in Google Maps</a>}
                </section>
              )}
              <section className="card r">
                <div className="ttl"><div className="i"><Icona nome="freccia" /></div><h2>Prossimi giorni</h2><button type="button" className="link" onClick={() => imposta({ vista: 'mese', mese: isoMese({ anno: d.getFullYear(), mese: d.getMonth() }) })}>Mese →</button></div>
                <div className="next">
                  {/* dice fin dove guarda: erano solo i giorni già caricati, ma il messaggio sembrava valere per sempre */}
                  {prossimi.length === 0 && statoVoci === 'pronto' && (
                    <p className="vuoto">Niente nei prossimi {quantiTesto(giorniAvanti, 'giorno', 'giorni')}.{giorniAvanti > 0 && <> Più in là, guardate nel <button type="button" className="link" onClick={() => imposta({ vista: 'mese', mese: isoMese({ anno: d.getFullYear(), mese: d.getMonth() }) })}>mese</button>.</>}</p>
                  )}
                  {prossimi.length === 0 && statoVoci !== 'pronto' && <StatoRiquadro stato={statoVoci} cosa="i prossimi giorni" onRiprova={ricarica} nome="prossimi" />}
                  {prossimi.map((v) => (
                    <div key={v.id}>
                      <div className="d">{daIso(v.data).getDate()}<small>{GIORNI_BREVI[daIso(v.data).getDay()]}</small></div>
                      <i className={`dot c-${v.tipo}`} />
                      {v.immobile?.foto && <img className="mini" src={v.immobile.foto} alt="" loading="lazy" />}
                      <div style={{ minWidth: 0 }}>
                        {v.cosa ? <CosaVoce cosa={v.cosa} tipo={v.tipo} voce={v} /> : linkVoce(v) ? <Link to={linkVoce(v)}><b>{v.titolo}</b></Link> : <b>{v.titolo}</b>}
                        {v.immobile?.titolo && <span className="imm-titolo">{v.immobile.titolo}</span>}
                        <span>{[v.ora, v.assegnata?.nome, v.cosa ? null : v.cliente_breve].filter(Boolean).join(' · ')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

const differenza = (a, b) => Math.round((daIso(a) - daIso(b)) / 86400000);

export default Agenda;
